import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Divider,
} from "@mui/material";
import StudentAutocomplete from "./StudentAutocomplete";
import { assignPacketToStudents } from "../../utils/assignments/assignPacketToStudents";
import useTutorStudents from "../../hooks/useTutorStudents";
import useAuth from "../../hooks/useAuth";

const ShareAssignmentModal = ({ open, onClose, assignment, onAssigned }) => {
  const { user } = useAuth();
  const { students, loading } = useTutorStudents();
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const handleClose = () => {
    setSelectedStudents([]);
    onClose();
  };

  const handleShare = async () => {
    if (!assignment || selectedStudents.length === 0) return;

    try {
      setSubmitting(true);

      await assignPacketToStudents({
        packet: assignment,
        studentIds: selectedStudents,
        tutorUid: user.uid,
      });

      // 🔥 Optimistic UI update
      onAssigned?.(assignment.id, selectedStudents.length);

      handleClose();
    } catch (err) {
      console.error("Failed to assign packet:", err);
    } finally {
      setSubmitting(false);
    }
  };

  if (!assignment) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ fontWeight: 600 }}>
        Share Assignment: {assignment.title}
      </DialogTitle>

      <DialogContent>
        <Box mb={2}>
          <Typography variant="subtitle1" fontWeight={600} mb={1}>
            Select Students
          </Typography>

          <StudentAutocomplete
            students={students}
            value={selectedStudents}
            onChange={setSelectedStudents}
            disabled={loading}
          />
        </Box>

        <Divider />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          disabled={submitting}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 999,
            px: 3,
          }}
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          onClick={handleShare}
          disabled={selectedStudents.length === 0 || submitting}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 999,
            px: 3,
          }}
        >
          {submitting ? "Sharing…" : "Share"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShareAssignmentModal;
